import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Tag,
  Space,
  Typography,
  Button,
  Row,
  Col,
  Divider,
  Spin,
  Alert,
  Avatar
} from "antd";
import {
  ArrowLeftOutlined,
  DollarOutlined,
  BookOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { getPaymentLedgerById } from "PlatForms/SelfLMS/Services/PaymentLedger/PaymentLedgerService";
import { PaymentStatusEnum, PaymentStatusLabels, PaymentStatusColors } from "Common/Constants/Enums/PaymentStatusEnum";
import { PaymentSourceTypeColors } from "Common/Constants/Enums/PaymentSourceTypeEnum";
import dayjs from "dayjs";
import { toast } from "react-toastify";

const { Title, Text } = Typography;

// Fix for the PaymentSourceTypeLabels - correcting MentorService to ExpertService
const FixedPaymentSourceTypeLabels = {
  0: "Course Purchase",
  1: "Expert Service",
  2: "Certificate",
  99: "Other"
};

const PaymentLedgerDetailPage = () => {
  const { ledgerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPaymentDetails();
  }, [ledgerId]);

  const loadPaymentDetails = async () => {
    setLoading(true);
    try {
      const data = await getPaymentLedgerById(ledgerId);
      console.log("..................payment details ", data);
      setPayment(data);
      setError(null);
    } catch (error) {
      console.error("Error loading payment details:", error);
      setError("Failed to load payment details");
      toast.error("Failed to load payment details");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "USD") =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    }).format(amount);

  const formatDate = (dateString) => (dateString ? dayjs(dateString).format("DD MMM YYYY, HH:mm:ss") : "Not paid yet");

  const getStatusIcon = (status) => {
    switch (status) {
      case PaymentStatusEnum.Pending:
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case PaymentStatusEnum.Paid:
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case PaymentStatusEnum.Canceled:
      case PaymentStatusEnum.Failed:
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return null;
    }
  };

  // Format minutes to hours (for expert service pricing)
  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes <= 0) return "Not specified";
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours > 0 && remainingMinutes > 0) {
      return `${hours}h${remainingMinutes}m`;
    }
    if (hours > 0) {
      return `${hours}h`;
    }
    return `${remainingMinutes}m`;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Loading payment details...</Text>
        </div>
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div style={{ padding: '40px' }}>
        <Alert
          message="Error"
          description={error || "Payment not found"}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={() => navigate("/selfLearner-dashboard/payment-ledger")}>
              Back to Payments
            </Button>
          }
        />
      </div>
    );
  }

  // Determine the type of purchase based on sourceType
  const isExpertServicePurchase = payment.sourceType === 1 && payment.expertServicePurchase !== null;
  const isCoursePurchase = payment.sourceType === 0 && payment.course !== null;
  const isCertificatePurchase = payment.sourceType === 2;
  const isOtherPurchase = payment.sourceType === 99;

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/selfLearner-dashboard/payment-ledger")}
          style={{ marginBottom: 16 }}
        >
          Back to Payments
        </Button>

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ minWidth: 250, marginBottom: 12 }}>
            <Title level={3} style={{ marginBottom: 4 }}>
              <DollarOutlined style={{ marginRight: 12, color: '#9A6B0E' }} />
              Payment Transaction Details
            </Title>
          </div>

          <Space wrap>
            <Tag color={PaymentSourceTypeColors[payment.sourceType]}>
              {FixedPaymentSourceTypeLabels[payment.sourceType]}
            </Tag>
            <Space>
              {getStatusIcon(payment.status)}
              <Tag color={PaymentStatusColors[payment.status]}>
                {PaymentStatusLabels[payment.status]}
              </Tag>
            </Space>
          </Space>
        </div>
      </div>

      {/* Main Content - All in one page */}
      <Row gutter={[24, 24]}>
        {/* Payment Information */}
        <Col xs={24} sm={24} md={12}>
          <Card title={<Space><DollarOutlined /> Payment Information</Space>}>
            <Descriptions column={1} size="middle" labelStyle={{ fontWeight: 500 }}>
              <Descriptions.Item label="Amount">
                <Text strong style={{ fontSize: '24px', color: '#9A6B0E' }}>
                  {formatCurrency(payment.amount, payment.currency)}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item label="Payment Status">
                <Space>
                  {getStatusIcon(payment.status)}
                  <Text strong>{PaymentStatusLabels[payment.status]}</Text>
                </Space>
              </Descriptions.Item>

              <Descriptions.Item label="Payment For">
                <Tag color={PaymentSourceTypeColors[payment.sourceType]}>
                  {FixedPaymentSourceTypeLabels[payment.sourceType]}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Paid At">
                <Space>
                  <CalendarOutlined />
                  <Text>{formatDate(payment.paidAt)}</Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Purchase Information - Dynamic based on purchase type (moved from third position) */}
        {isCoursePurchase && (
          <Col xs={24} sm={24} md={12}>
            <Card title={<Space><BookOutlined /> Course Information</Space>}>
              <div style={{ marginBottom: 20 }}>
                <Title level={4} style={{ marginBottom: 8 }}>
                  {payment.course?.title}
                </Title>
                <Text type="secondary">
                  Course ID: {payment.course?.id}
                </Text>
              </div>

              <Divider />

              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Description:</Text>
                <Text style={{ lineHeight: 1.6 }}>
                  {payment.course?.description}
                </Text>
              </div>

              <Descriptions column={1} size="small">
                <Descriptions.Item label="Duration">
                  <Text>{payment.course?.estimatedTime || "Not specified"}</Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        )}

        {isExpertServicePurchase && (
          <Col xs={24} sm={24} md={12}>
            <Card title={<Space><TeamOutlined /> Expert Service Information</Space>}>
              <div style={{ marginBottom: 20 }}>
                <Title level={4} style={{ marginBottom: 8 }}>
                  {payment.expertServicePurchase?.expertService?.title}
                </Title>
                <Text type="secondary">
                  Service ID: {payment.expertServicePurchase?.expertService?.id}
                </Text>
              </div>

              <Divider />

              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Description:</Text>
                <Text style={{ lineHeight: 1.6 }}>
                  {payment.expertServicePurchase?.expertService?.description}
                </Text>
              </div>

              <Descriptions column={1} size="small">
                <Descriptions.Item label="Units Purchased">
                  <Text>{payment.expertServicePurchase?.unitsPurchased}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Units Consumed">
                  <Text>{payment.expertServicePurchase?.unitsConsumed}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Total Minutes">
                  <Text>{payment.expertServicePurchase?.totalMinutes} minutes</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Consumed Minutes">
                  <Text>{payment.expertServicePurchase?.consumedMinutes} minutes</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Unit Price">
                  <Text strong>
                    ${payment.expertServicePurchase?.expertService?.pricing?.unitPrice}/
                    {formatMinutesToHours(payment.expertServicePurchase?.expertService?.pricing?.unitDurationMinutes)}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Expert">
                  <Space>
                    <Avatar 
                      size="small" 
                      src={payment.expertServicePurchase?.expertService?.expert?.profileImage} 
                      icon={<UserOutlined />}
                    />
                    <Text>
                      {payment.expertServicePurchase?.expertService?.expert?.firstName} {payment.expertServicePurchase?.expertService?.expert?.lastName}
                    </Text>
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        )}

        {isCertificatePurchase && (
          <Col xs={24} sm={24} md={12}>
            <Card title={<Space><BookOutlined /> Certificate Information</Space>}>
              <Alert
                message="Certificate Information"
                description="Certificate purchase information will be displayed here."
                type="info"
                showIcon
              />
            </Card>
          </Col>
        )}

        {isOtherPurchase && (
          <Col xs={24} sm={24} md={12}>
            <Card title={<Space><BookOutlined /> Other Information</Space>}>
              <Alert
                message="Other Purchase Information"
                description="Other purchase information will be displayed here."
                type="info"
                showIcon
              />
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default PaymentLedgerDetailPage;